import { tva } from '@gluestack-ui/utils/nativewind-utils'
import { AnimatePresence, Motion, MotionComponentProps } from '@legendapp/motion'
import { cssInterop } from 'nativewind'
import React, { useEffect, useState } from 'react'
import { View, ViewStyle } from 'react-native'

import { Box } from '../box'
import { Pressable } from '../pressable'
import { Text } from '../text'

/**
 * Initial status-reveal overlay shown when a dashboard widget gains a warning /
 * alert status (AOS-638): a ripple in the status colour scales up to cover the
 * card, the title + reasons fade in, then it auto-hides. Built on
 * `@legendapp/motion` (the cross-platform motion primitive used by the gluestack
 * Tooltip / Modal / Actionsheet) so the same animation runs on web and React
 * Native — no styled-components, no platform-specific code.
 */
export type WidgetStatusLevel = 'success' | 'warning' | 'critical' | 'neutral'

type MotionViewProps = React.ComponentProps<typeof View> &
    MotionComponentProps<typeof View, ViewStyle, unknown, unknown, unknown>

const MotionView = Motion.View as React.ComponentType<MotionViewProps>
cssInterop(MotionView, { className: 'style' })

const ripple = tva({
    base: 'absolute -left-[70px] -top-[70px] h-[200px] w-[200px] rounded-full',
    variants: {
        status: {
            success: 'bg-status-success',
            warning: 'bg-status-warning',
            critical: 'bg-status-critical',
            neutral: 'bg-status-neutral',
        },
    },
})

const RIPPLE_MS = 700
const DEFAULT_AUTO_HIDE_MS = 4000

export interface WidgetStatusOverlayProps {
    status: WidgetStatusLevel
    title: React.ReactNode
    reasons?: string[]
    /** Time the overlay stays open before auto-hiding (ms). */
    autoHideMs?: number
    /** Called once the overlay has fully hidden. */
    onEnd?: () => void
}

export const WidgetStatusOverlay: React.FC<WidgetStatusOverlayProps> = ({
    status,
    title,
    reasons = [],
    autoHideMs = DEFAULT_AUTO_HIDE_MS,
    onEnd,
}) => {
    const [visible, setVisible] = useState(true)

    useEffect(() => {
        const hideTimer = setTimeout(() => setVisible(false), autoHideMs)
        const endTimer = setTimeout(() => onEnd?.(), autoHideMs + RIPPLE_MS)
        return () => {
            clearTimeout(hideTimer)
            clearTimeout(endTimer)
        }
    }, [autoHideMs, onEnd])

    // Warning ripple is light (yellow) → dark ink; the rest are dark → light ink.
    const bodyTone = status === 'warning' ? 'on-graphics' : 'logotype'

    return (
        <AnimatePresence>
            {visible && (
                <Box className='absolute inset-0 z-10 overflow-hidden rounded-lg'>
                    <MotionView
                        className={ripple({ status })}
                        initial={{ scale: 0.1, opacity: 0 }}
                        animate={{ scale: 20, opacity: 1 }}
                        exit={{ scale: 0.1, opacity: 0 }}
                        transition={{ type: 'timing', duration: RIPPLE_MS }}
                    />
                    <Pressable
                        className='absolute inset-0 items-center justify-center p-4'
                        onPress={() => setVisible(false)}
                    >
                        <MotionView
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ type: 'timing', duration: RIPPLE_MS }}
                        >
                            <Text
                                size='heading'
                                weight='medium'
                                tone={bodyTone}
                                className='text-center'
                            >
                                {title}
                            </Text>
                            {reasons.map((reason, index) => (
                                <Text
                                    key={`${reason}-${index}`}
                                    size='body'
                                    tone={bodyTone}
                                    className='text-center'
                                >
                                    {reason}
                                </Text>
                            ))}
                        </MotionView>
                    </Pressable>
                </Box>
            )}
        </AnimatePresence>
    )
}

WidgetStatusOverlay.displayName = 'WidgetStatusOverlay'
